/**
 * App.tsx - Main Application Component
 *
 * Refactored to use React Contexts for state management and
 * extracted components for better organization.
 *
 * Phase 3.1: Tab components are lazy-loaded for better initial bundle size.
 */

import React, { useState, useEffect, Suspense, lazy } from 'react';
import type { Particle, TabId } from './types';

// Contexts
import { ProcessingProvider, useProcessing } from './contexts/ProcessingContext';
import { VaultProvider, useVault } from './contexts/VaultContext';

// Components (eagerly loaded - small/critical)
import { Starfield } from './components/Starfield';
import { LoadingSpinner } from './components/LoadingSpinner';
import { ErrorDisplay } from './components/ErrorDisplay';
import { BlackHole } from './components/BlackHole';
import { TabNavigation } from './components/TabNavigation';
import { FeaturesSection } from './components/FeaturesSection';
import { StorageWarningModal, useStorageWarning } from './components/StorageWarningModal';

// Tab components (lazy-loaded - large)
const ProductionUploadTab = lazy(() =>
  import('./components/UploadTab').then((m) => ({ default: m.UploadTab }))
);
const ProductionVaultTab = lazy(() =>
  import('./components/VaultTab').then((m) => ({ default: m.VaultTab }))
);
const ProductionVerifyTab = lazy(() =>
  import('./components/VerifyTab').then((m) => ({ default: m.VerifyTab }))
);

// Services
import { useStorage } from './services/storage.service';
import { verifyHash } from './utils';

// ============================================================================
// Constants
// ============================================================================

const PARTICLE_COUNT = 50;

// ============================================================================
// Header Component (Simple, inline is fine)
// ============================================================================

interface HeaderProps {
  onInfoClick: () => void;
}

const Header: React.FC<HeaderProps> = ({ onInfoClick }) => (
  <header className="relative z-10 p-6 border-b border-purple-500/30">
    <div className="max-w-6xl mx-auto flex items-center justify-between">
      <div>
        <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-purple-400 via-pink-500 to-blue-500 bg-clip-text text-transparent">
          Avent Horizon Vault
        </h1>
        <p className="text-gray-400 mt-1 text-sm sm:text-base">
          Where Information Meets the Event Horizon
        </p>
      </div>
      <button
        onClick={onInfoClick}
        className="p-2 hover:bg-purple-500/20 rounded-full transition-colors duration-200 focus-visible-ring"
        aria-label="Learn about the event horizon paradox"
      >
        <svg className="w-6 h-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </button>
    </div>
  </header>
);

// ============================================================================
// Info Modal Component
// ============================================================================

interface InfoModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const InfoModal: React.FC<InfoModalProps> = ({ isOpen, onClose }) => {
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 animate-fade-in"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="info-modal-title"
    >
      <div
        className="glass-card rounded-xl max-w-2xl w-full p-6 sm:p-8 relative animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors p-1 rounded-full hover:bg-white/10 focus-visible-ring"
          aria-label="Close modal"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <h2 id="info-modal-title" className="text-2xl font-bold mb-4 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
          The Event Horizon Information Paradox
        </h2>

        <div className="space-y-4 text-gray-300">
          <p>
            In black hole physics, the <strong className="text-purple-300">event horizon information paradox</strong> asks:
            when information crosses the event horizon, is it truly lost forever?
          </p>

          <div className="bg-purple-900/20 border border-purple-500/30 rounded-lg p-4 space-y-2">
            <p className="flex items-start gap-2">
              <span className="text-purple-400 mt-1">&#x2022;</span>
              <span><strong className="text-purple-300">Classical relativity</strong> says information is destroyed</span>
            </p>
            <p className="flex items-start gap-2">
              <span className="text-pink-400 mt-1">&#x2022;</span>
              <span><strong className="text-pink-300">Quantum mechanics</strong> insists it must be preserved</span>
            </p>
            <p className="flex items-start gap-2">
              <span className="text-blue-400 mt-1">&#x2022;</span>
              <span><strong className="text-blue-300">Holographic principle</strong> suggests it is encoded on the horizon itself</span>
            </p>
          </div>

          <p>
            This vault mirrors that paradox: your data appears <em>"destroyed"</em> through
            cryptographic hashing, yet remains verifiable through mathematical proofs -
            preserved eternally on the digital event horizon.
          </p>

          <p className="text-sm text-gray-400 pt-2 border-t border-gray-700">
            Just as Hawking radiation theoretically carries information out of a black hole,
            your hash serves as the cryptographic "radiation" proving your data existed.
          </p>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Footer Component
// ============================================================================

const Footer: React.FC = () => (
  <footer className="relative z-10 mt-16 p-6 border-t border-purple-500/30 text-center text-gray-500 text-sm">
    <p className="mb-2">
      <span className="text-purple-400">AventHorizon.com</span>
      {' '}&bull;{' '}
      Where Classical Meets Quantum
      {' '}&bull;{' '}
      Information Never Dies
    </p>
    <p className="text-xs text-gray-600">
      Cryptographic operations performed locally in your browser. No data is transmitted.
    </p>
  </footer>
);

// ============================================================================
// Tab Loading Fallback (for lazy-loaded tabs)
// ============================================================================

const TabLoadingFallback: React.FC = () => (
  <div className="flex flex-col items-center justify-center py-12 space-y-4">
    <div className="relative w-12 h-12">
      <div className="absolute inset-0 rounded-full border-2 border-purple-500/30 animate-pulse" />
      <div className="absolute inset-2 rounded-full border-2 border-purple-400/50 animate-spin" style={{ borderTopColor: 'transparent' }} />
    </div>
    <p className="text-gray-400 text-sm">Loading...</p>
  </div>
);

// ============================================================================
// App Content Component (inside providers)
// ============================================================================

const AppContent: React.FC = () => {
  const { isReady: storageReady, error: storageError } = useStorage();
  const { showWarning, closeWarning, dismissPermanently } = useStorageWarning();
  const { isProcessing, processingStage, setIsProcessing, setProcessingStage } = useProcessing();
  const { vaultedItems, addVault } = useVault();

  // UI State
  const [activeTab, setActiveTab] = useState<TabId>('upload');
  const [showInfo, setShowInfo] = useState(false);
  const [particles, setParticles] = useState<Particle[]>([]);

  // Generate starfield particles on mount
  useEffect(() => {
    const newParticles: Particle[] = Array.from({ length: PARTICLE_COUNT }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 3 + 1,
      duration: Math.random() * 20 + 10,
      delay: Math.random() * 5,
      opacity: Math.random() * 0.5 + 0.3
    }));
    setParticles(newParticles);
  }, []);

  // Render tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'upload':
        return (
          <ProductionUploadTab
            isProcessing={isProcessing}
            processingStage={processingStage}
            onProcessingChange={setIsProcessing}
            onProcessingStageChange={setProcessingStage}
            onVaultCreated={addVault}
          />
        );
      case 'verify':
        return (
          <ProductionVerifyTab
            vaultedItems={vaultedItems}
            onVerify={async (data, expectedHash, algorithm, salt) => {
              return verifyHash(data, expectedHash, algorithm, salt);
            }}
          />
        );
      case 'vault':
        return <ProductionVaultTab />;
      default:
        return null;
    }
  };

  // Show loading while storage initializes
  if (!storageReady) {
    return <LoadingSpinner />;
  }

  // Show error if storage failed
  if (storageError) {
    return (
      <ErrorDisplay
        error={{
          message: storageError.message || 'Failed to initialize secure storage',
          code: 'STORAGE_INIT_ERROR',
          timestamp: new Date().toISOString()
        }}
        onRetry={() => window.location.reload()}
      />
    );
  }

  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden relative">
      {/* Background */}
      <Starfield particles={particles} />

      {/* Header */}
      <Header onInfoClick={() => setShowInfo(true)} />

      {/* Modals */}
      <InfoModal isOpen={showInfo} onClose={() => setShowInfo(false)} />
      <StorageWarningModal
        isOpen={showWarning}
        onClose={closeWarning}
        onDismissPermanently={dismissPermanently}
      />

      {/* Main Content */}
      <main className="relative z-10 max-w-6xl mx-auto p-4 sm:p-6 mt-4 sm:mt-8">
        <div className="grid lg:grid-cols-2 gap-6 lg:gap-8">
          {/* Black Hole Visualization */}
          <div className="flex items-center justify-center order-2 lg:order-1 py-8 lg:py-0">
            <BlackHole isProcessing={isProcessing} />
          </div>

          {/* Controls */}
          <div className="order-1 lg:order-2">
            <TabNavigation
              activeTab={activeTab}
              onTabChange={setActiveTab}
            />

            <div
              className="glass-card rounded-xl p-4 sm:p-6"
              role="tabpanel"
              id={`tabpanel-${activeTab}`}
            >
              <Suspense fallback={<TabLoadingFallback />}>
                {renderTabContent()}
              </Suspense>
            </div>
          </div>
        </div>

        {/* Features Section */}
        <FeaturesSection />
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
};

// ============================================================================
// Main App Component (Provider wrapper)
// ============================================================================

const App: React.FC = () => {
  return (
    <ProcessingProvider>
      <VaultProvider>
        <AppContent />
      </VaultProvider>
    </ProcessingProvider>
  );
};

export default App;
